import { useMemo, type FC } from "react";
import { useParams } from "react-router-dom";
import { Heading2, Heading3 } from "@/components/podkit/typography/Headings";
import { useGetWorkflow, useGetWorkflowExecutionAction } from "@/queries/automation-queries";
import { ErrorMessage } from "@/components/ErrorMessage";
import { WorkflowExecutionActionStepList } from "@/components/automations/AutomationExecutionActionStepList";
import { getProjectIDFromContext, workflowName } from "@/utils/automations";
import { Banner } from "@/components/flexkit/Banner";
import { useListProjects } from "@/queries/project-queries";
import { WorkflowExecutionActionStatusPill } from "@/components/automations/AutomationExecutionActionStatusPill";
import { WorkflowExecutionActionDropdownMenu } from "@/components/automations/AutomationExecutionActionDropdownMenu";
import { useGetAgentExecution } from "@/queries/agent-queries";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { ChatMessages } from "@/components/ai/chat/ChatMessages";
import { Card } from "@/components/flexkit/Card";
import { WorkflowExecutionAction_Status_StepStatus_StepPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleDotted } from "central-icons/IconCircleDotted";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import { Text } from "@/components/podkit/typography/Text";
import { ScrollArea } from "@/components/flexkit/ScrollArea";

export const AutomationExecutionActionPage: FC = () => {
    const { workflowExecutionActionId } = useParams<{
        workflowExecutionActionId: string;
    }>();
    const { data: projects } = useListProjects({ enabled: true });
    const { data: workflowExecutionAction, error: workflowExecutionActionError } =
        useGetWorkflowExecutionAction(workflowExecutionActionId);
    const { data: workflow } = useGetWorkflow(workflowExecutionAction?.metadata?.workflowId);

    const projectId = getProjectIDFromContext(workflowExecutionAction?.spec?.context);
    const project = projects?.projects.find((p) => p.id === projectId);

    const { data: agentExecution } = useGetAgentExecution(workflowExecutionAction?.status?.agentExecutionId);
    const { messages } = useStreamConversation(
        agentExecution?.id,
        agentExecution?.status?.conversationUrl,
        [], // no pending messages as the conversation is driven by the workflow
    );

    const counters = useMemo(() => {
        let completed = 0;
        let failed = 0;
        let incomplete = 0;
        workflowExecutionAction?.status?.stepStatuses.forEach((step) => {
            switch (step.phase) {
                case WorkflowExecutionAction_Status_StepStatus_StepPhase.DONE:
                    completed++;
                    break;
                case WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED:
                    failed++;
                    break;
                default:
                    incomplete++;
            }
        });
        return { completed, failed, incomplete };
    }, [workflowExecutionAction?.status?.stepStatuses]);

    return (
        <div data-testid="workflow-execution-action-page" className="flex size-full flex-col gap-4 pl-4">
            <ErrorMessage error={workflowExecutionActionError} />

            <div className="flex items-center justify-between gap-1">
                <Heading2>
                    &quot;{workflowExecutionAction?.metadata?.actionName ?? <span>-</span>}
                    <span>&quot; Action Report</span>
                </Heading2>
                {workflowExecutionAction && (
                    <div className="flex items-center gap-2">
                        <WorkflowExecutionActionStatusPill action={workflowExecutionAction} />
                        <WorkflowExecutionActionDropdownMenu action={workflowExecutionAction} />
                    </div>
                )}
            </div>

            {workflowExecutionAction?.status?.failureMessage && (
                <Banner variant="warning" text={workflowExecutionAction.status.failureMessage} />
            )}

            <Card
                variant={"bordered"}
                className="flex min-h-[106px] flex-col overflow-hidden border bg-surface-secondary p-0 shadow-sm"
            >
                {/* HEADER */}
                <div className="border-b border-border-base p-4">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex flex-col gap-2.5">
                            <Heading3 className="text-lg font-medium leading-[18px]">
                                {workflowName(workflow)} / {project?.metadata?.name}
                            </Heading3>
                            {/* TODO(mads): In the future we want to show the duration of the action here but we don't have that information right now */}
                        </div>
                        <div className="flex gap-1.5 py-0.5">
                            {counters.completed > 0 && (
                                <div className="flex items-center gap-1">
                                    <IconCircleCheck size={16} className="text-content-success" />
                                    <Text className="text-sm text-content-success">{counters.completed} completed</Text>
                                </div>
                            )}
                            {counters.failed > 0 && (
                                <div className="flex items-center gap-1">
                                    <IconTriangleExclamation size={16} className="text-content-warning" />
                                    <Text className="text-sm text-content-warning">{counters.failed} failed</Text>
                                </div>
                            )}
                            {counters.incomplete > 0 && (
                                <div className="flex items-center gap-1">
                                    <IconCircleDotted size={16} className="text-content-muted" />
                                    <Text className="text-sm text-content-muted">{counters.incomplete} incomplete</Text>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* CONTENT */}
                <div className="flex flex-col overflow-hidden">
                    <ScrollArea
                        orientation="both"
                        id="workflow-execution-action-conversation-content"
                        className="m-0.5 mb-1 max-h-full flex-grow p-4"
                    >
                        {workflowExecutionAction && (
                            <div className="flex flex-col gap-4">
                                <WorkflowExecutionActionStepList action={workflowExecutionAction} />
                                {agentExecution && (
                                    <ChatMessages
                                        agentExecution={agentExecution}
                                        messages={messages}
                                        className="h-auto max-w-none grow self-stretch rounded-lg border border-border-base bg-surface-base p-4"
                                    />
                                )}
                            </div>
                        )}
                    </ScrollArea>
                </div>
            </Card>
        </div>
    );
};
